import useSwrImmutable from 'swr/immutable'
import { API_URL } from '@/utils/constants'
import { getRarityTier, RarityTierStyle } from '@/utils/hooks/useCollectionRarity'
import { isCollectionObjectSlug } from '@/utils/collection-object'

/**
 * Per-inscription rank within a collection. Rank 1 = rarest.
 * Tier is derived from the inscription's percentile within the collection,
 * using the same Exotic/Legendary/Epic/Rare/Uncommon/Common thresholds we use
 * for individual traits.
 */
interface InscriptionRank {
	rank: number
	score: number
	total: number
	tied: number
	tier: RarityTierStyle | null
}

export interface CollectionRanksData {
	total: number
	ranks: Record<string, InscriptionRank>
}

const SKIP_SLUGS = new Set(['%5Bid%5D', 'sub-100k', 'sub-1k'])

/**
 * Standard rarity-score formula (a.k.a. "trait normalization"):
 *   For each trait T with value V used by N items out of S total,
 *   weight(T,V) = S / N    (lower N = rarer = higher weight)
 *   score(item) = Σ weight(T,V) over the item's traits
 * Items with more traits do tend to score higher, which mirrors community
 * conventions where having traits at all (vs "None") is considered rarer.
 */
export const computeRanks = (
	inscriptions: any[],
	traitCounts: Record<string, Record<string, number>>,
	totalSupply: number
): Record<string, InscriptionRank> => {
	const scored = inscriptions
		.map((insc) => {
			const attrs = insc?.meta?.attributes
			if (!Array.isArray(attrs) || attrs.length === 0) {
				return { id: insc.id, score: 0 }
			}
			const weights: number[] = []
			for (const a of attrs) {
				const t = String(a?.trait_type || '').toLowerCase()
				const v = String(a?.value ?? '').toLowerCase()
				const count = traitCounts[t]?.[v]
				if (count && count > 0) {
					weights.push(totalSupply / count)
				}
			}
			return { id: insc.id, score: weights.sort((a, b) => a - b).reduce((sum, w) => sum + w, 0) }
		})
		.filter((x) => !!x.id)

	scored.sort((a, b) => b.score - a.score)

	const out: Record<string, InscriptionRank> = {}
	const total = scored.length
	const ties = new Map<number, number>()
	for (const s of scored) ties.set(s.score, (ties.get(s.score) || 0) + 1)
	let rank = 1
	for (let i = 0; i < scored.length; i++) {
		const s = scored[i]
		if (i === 0 || s.score !== scored[i - 1].score) rank = i + 1
		// Percentile = rank / total * 100. Top 1% = Exotic, next to 5% = Legendary, etc.
		const percentile = (rank / total) * 100
		out[s.id] = {
			rank,
			score: s.score,
			total,
			tied: ties.get(s.score) || 1,
			tier: getRarityTier(percentile)
		}
	}
	return out
}

const fetcher = async ([_k, slug]: [string, string]): Promise<CollectionRanksData | null> => {
	if (!slug || SKIP_SLUGS.has(slug)) return null
	if (isCollectionObjectSlug(slug)) return null

	// Probe attributes first. Collections with no traits (e.g. Runestone
	// returns [] here) can't have meaningful rarity — skip the heavy
	// /inscriptions fetch entirely.
	const attrsRes = await fetch(`${API_URL}/collection/${encodeURIComponent(slug)}/attributes`)
	if (!attrsRes.ok) return null
	const attrs = await attrsRes.json().catch(() => null)
	if (!Array.isArray(attrs) || attrs.length === 0) return null

	const inscRes = await fetch(`${API_URL}/collection/${encodeURIComponent(slug)}/inscriptions`)
	if (!inscRes.ok) return null
	const inscData = await inscRes.json().catch(() => null)

	const arr: any[] = Array.isArray(inscData) ? inscData : inscData?.inscriptions || []
	if (arr.length === 0) return null

	const statsRes = await fetch(`${API_URL}/collection/${encodeURIComponent(slug)}/stats`)
	const stats = statsRes.ok ? await statsRes.json().catch(() => null) : null

	const totalSupply: number = stats?.total_supply || arr.length

	const traitCounts: Record<string, Record<string, number>> = {}
	for (const group of attrs as Array<{ trait_type: string; values: Array<{ value: any; count: number }> }>) {
		const inner: Record<string, number> = {}
		for (const v of group.values || []) {
			inner[String(v.value).toLowerCase()] = v.count
		}
		traitCounts[String(group.trait_type).toLowerCase()] = inner
	}

	// If somehow we got here with no usable trait counts (or only one
	// possible value per trait), rarity scores would be uniform — bail.
	if (Object.keys(traitCounts).length === 0) return null

	const ranks = computeRanks(arr, traitCounts, totalSupply)
	// Final guard: if every inscription got the same score, ranking is
	// meaningless (e.g. all inscriptions actually have no traits).
	const scores = Object.values(ranks).map((r) => r.score)
	if (scores.length > 0 && scores.every((s) => s === scores[0])) return null

	return { total: Object.keys(ranks).length, ranks }
}

export default function useCollectionRanks(slug?: string) {
	return useSwrImmutable<CollectionRanksData | null>(
		slug ? ['collection-ranks', slug] : null,
		fetcher
	)
}
